// Players at platform level — HQ's search and HQ's view of one person (spec/14 §10, F-HQ-28).
//
//   GET  /v1/hq/players                  search every player on the Platform      hq:players_read
//   GET  /v1/hq/players/{playerId}       one player's platform record             hq:players_read
//   POST /v1/hq/players/{playerId}/ban   the platform ban (`player-bans.ts`)      hq:players
//   POST /v1/hq/players/{playerId}/unban lifting it (`player-bans.ts`)            hq:players
//
// **Reading and banning are two permissions** (the product owner, 2026-09-22): `hq:players_read` —
// `hq_owner`, `hq_admin` and `hq_support` — finds and opens a player; `hq:players` — `hq_owner`
// alone (#438) — bans. The view carries no "may I ban" field: a screen asks `GET /v1/me/access`,
// which at platform scope lists the caller's `hq:*` permissions from the same table the guard reads
// (spec/07 §6.3), and hides the action when `hq:players` is not among them.
//
// **Platform-level data only.** What these answer is the player's own record, which belongs to
// nobody but the player (spec/03 §11, spec/20 §6): the profile, how they sign in, the devices they
// have signed in on, and the platform ban. An organisation's dealings with the player — its sessions,
// orders, wallet, notes and tags, and its own bans — are that organisation's, and are not here. HQ
// reads them only as `organization.hqAccess` allows, with a reason, one audit row per organisation
// read (spec/07 §7, spec/14 §10); that door is built with F-HQ-02, which adds `hqAccess`.
//
// **A search needs something to search for.** `q` is required: HQ looks a person up, it does not
// browse the Platform's players. A phone number in E.164 and an email address match **whole**, never
// in part, exactly as an organisation's search matches them (spec/08 §4.13) — and with the answers in
// full, a partial match would hand out every number that shares a few digits. A player id
// (`ply_…`, what the member QR says) matches that one player. Anything else is matched against the
// name, the display name and the gamer tag.
//
// **Contact details are in full.** Every HQ answer carries the phone number and the email address
// unmasked to anyone who may read it at all: "HQ should have full control, unrestricted" (the
// product owner, 2026-09-22; spec/20 §6 carves HQ's player reads out of its masking rule). A
// synthetic `.invalid` address is still never shown: it is not an address anybody gave.
//
// **Every read is audited** — accountability, not a restriction. A search writes one platform-level
// row (`identity.user.search`) with what was searched for, masked like any personal data in the log,
// and how many people it found; the view writes one (`identity.user.read`) naming the person. No
// typed reason is asked for (the product owner, 2026-09-22): this is the player's platform-level data,
// not an organisation's. A platform ban's reason is HQ's alone: it is in the view, and nowhere an
// organisation reads.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { PlayerIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_PLAYERS_READ } from "../permissions/index.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { PlatformBanSchema, PlatformPlayerParamsSchema } from "./player-bans.js";
// -------------------------------------------------------------------------------------------------
// Search
// -------------------------------------------------------------------------------------------------
export const HqPlayerSearchQuerySchema = PageQuerySchema.extend({
    q: z
        .string()
        .trim()
        .min(2)
        .max(100)
        .openapi({
        description: 'A phone number in E.164 (`+919876543210`), an email address or a player id (`ply_…`), matched exactly; ' +
            'anything else is matched against the name, display name and gamer tag.',
    }),
})
    // Unknown parameters are refused, never turned into a filter (spec/08 §2.4).
    .strict()
    .openapi('HqPlayerSearchQuery');
const hqPlayerShape = {
    id: PlayerIdSchema,
    /** The gamer tag, when the player has chosen one. */
    username: z.string().nullable(),
    displayName: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    /** E.164, in full. */
    phoneNumber: z.string().nullable(),
    /** In full. Never a synthetic address. */
    email: z.string().nullable(),
    /** Whether `phoneNumber` and `email` were masked for this caller. `false`: HQ sees them in full. */
    contactMasked: z.boolean(),
    /** Whether a platform ban stops this person signing in now. */
    banned: z.boolean(),
    /** When the player's account was made. */
    createdAt: IsoDateTimeSchema,
};
export const HqPlayerSummarySchema = z
    .object(hqPlayerShape)
    .openapi('HqPlayerSummary', { description: 'A player as HQ’s search finds them: platform-level data only (spec/14 §10).' });
export const HqPlayerListSchema = paginated(HqPlayerSummarySchema, 'HqPlayerList');
// -------------------------------------------------------------------------------------------------
// One player
// -------------------------------------------------------------------------------------------------
/**
 * How a person can sign in (spec/07 §3.1): a verified phone number (a code), a verified email address
 * (a code), a password, and each social account linked to them.
 */
export const HQ_PLAYER_SIGN_IN_METHODS = ['phone', 'email', 'password', 'google', 'discord', 'steam'];
export const HqPlayerSignInMethodSchema = z.enum(HQ_PLAYER_SIGN_IN_METHODS).openapi('HqPlayerSignInMethod');
/** The most recent devices the view lists. */
export const HQ_PLAYER_DEVICES_MAX = 20;
export const HqPlayerDeviceSchema = z
    .object({
    /** Browser family, OS family and kind of device, versions dropped: `chrome/windows/desktop` (spec/07 §3.3). */
    deviceKey: z.string(),
    firstSeenAt: IsoDateTimeSchema,
    lastSeenAt: IsoDateTimeSchema,
})
    .openapi('HqPlayerDevice', { description: 'A device the person has signed in on.' });
// A fresh object rather than `.extend(…)`: extending a registered component emits `allOf`, which the
// client generators refuse (tools/src/codegen).
export const HqPlayerSchema = z
    .object({
    ...hqPlayerShape,
    phoneNumberVerified: z.boolean(),
    /** `false` for a synthetic address, which is not an address anybody gave. */
    emailVerified: z.boolean(),
    signInMethods: z.array(HqPlayerSignInMethodSchema),
    twoFactorEnabled: z.boolean(),
    /** The devices the person last signed in on, most recent first, at most `HQ_PLAYER_DEVICES_MAX`. */
    devices: z.array(HqPlayerDeviceSchema),
    /** The platform ban on record, its reason included — HQ's alone. Set and lifted by `…/ban|unban`. */
    platformBan: PlatformBanSchema,
})
    .openapi('HqPlayer', {
    description: 'One player’s platform record as HQ sees it: the profile, how they sign in, their devices and the platform ' +
        'ban (spec/14 §10). No organisation’s dealings with them.',
});
// -------------------------------------------------------------------------------------------------
// The routes
// -------------------------------------------------------------------------------------------------
const forbidden = problemResponse('Not HQ, no `hq:players_read`, or no second factor on the session.', 'forbidden', 'second-factor-required');
export const listHqPlayersRoute = createRoute({
    method: 'get',
    path: '/v1/hq/players',
    operationId: 'listHqPlayers',
    tags: ['hq'],
    summary: 'Search every player on the Platform',
    description: 'By phone number, email address or player id (each matched whole), or by part of a name or gamer tag; oldest ' +
        'account first. Platform-level data only, contact details in full. The search is audited: one platform-level ' +
        'row with what was searched for. Platform scope: requires `hq:players_read` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_PLAYERS_READ,
    request: { query: HqPlayerSearchQuerySchema },
    responses: {
        200: jsonResponse(HqPlayerListSchema, 'A page of players.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getHqPlayerRoute = createRoute({
    method: 'get',
    path: '/v1/hq/players/{playerId}',
    operationId: 'getHqPlayer',
    tags: ['hq'],
    summary: 'One player’s platform record',
    description: 'The profile, how they sign in, the devices they have signed in on and the platform ban, its reason ' +
        'included; contact details in full. Ban and unban with `POST …/ban` and `…/unban`, which need `hq:players` ' +
        '(ask `GET /v1/me/access` whether you hold it). The read is audited: one platform-level row naming the ' +
        'person. Platform scope: requires `hq:players_read` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_PLAYERS_READ,
    request: { params: PlatformPlayerParamsSchema },
    responses: {
        200: jsonResponse(HqPlayerSchema, 'The player.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such player.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const HQ_PLAYER_ROUTES = [listHqPlayersRoute, getHqPlayerRoute];
//# sourceMappingURL=hq-players.js.map